/**
 * Category mapping utilities for automatic tag categorization and logo selection
 */

/**
 * Known category groups based on analysis
 */
export const knownCategoryGroups = {
  'Health, Safety & Wellness': ['Health', 'Safety', 'Wellness', 'Wellbeing', 'Mental Health', 'Emergency', 'Care', 'Support Services', 'Crisis', 'Prevention'],
  'Medical': ['Medical', 'Healthcare', 'Clinical', 'Nursing', 'Medicine', 'Patient', 'Treatment', 'Diagnosis', 'Therapy', 'Health Records'],
  'Academic Support': ['Student Academic Advising', 'Academic Advising', 'Student Success', 'Advising', 'Academic Support', 'Student Support', 'Tutoring', 'Academic', 'Academic Administration', 'Student and Academic Administration', 'Learning Support', 'Study Skills', 'Educational Support'],
  'Academic Advising': ['Academic Advising & Academic Success', 'Academic Success', 'Success Coaching', 'Academic Planning', 'Degree Planning', 'Course Planning', 'Student Progress'],
  'Admissions': ['Student Admissions', 'Admissions', 'Enrollment', 'Recruitment', 'Application', 'LSAC', 'Prospective Students', 'Enrollment Management', 'Application Processing', 'Student Recruitment'],
  'Advancement': ['Advancement', 'Alumni', 'Alumni Relations', 'Development', 'Fundraising', 'Giving', 'Donor', 'Donor Relations', 'Annual Fund', 'Major Gifts', 'Campaign', 'Philanthropy'],
  'Information and Communication Technology Management': ['Information and Communication Technology', 'ICT Management', 'ICT', 'Information Technology Management', 'Information Communication Technology'],
  'Campus Life & Student Experience': ['Campus Life', 'Student Experience', 'Student Activities', 'Campus Services', 'Recreation', 'Dining', 'Housing', 'Residence Life', 'Student Engagement', 'Campus Events', 'Student Organizations', 'Campus Community'],
  'Library': ['Library', 'Library Services', 'Library Management', 'Library Administration', 'Resource Management', 'Catalog', 'Research', 'Academic Resources', 'Digital Collections', 'Archives'],
  'Bookstore': ['Bookstore', 'Textbooks', 'Course Materials', 'Books', 'Supplies', 'Merchandise', 'Course Resources', 'Educational Materials', 'Academic Materials'],
  'Accessibility': ['Accessibility', 'Disability Services', 'ADA', 'Accommodations', 'Special Needs', 'Assistive Technology', 'Inclusive', 'Universal Design', 'Barrier-Free'],
  'Banking & Financial': ['Banking', 'Financial Services', 'Payments', 'Financial Aid', 'Billing', 'Tuition', 'Fees', 'Bursar', 'Cashier', 'Student Accounts', 'Accounts Payable'],
  'Career Services': ['Career Services', 'Career Development', 'Job Placement', 'Internships', 'Employment', 'Career Counseling', 'Professional Development', 'Job Search'],
  'Emergency Communication': ['Emergency', 'Crisis', 'Alert', 'Notification', 'Safety Communication', 'Emergency Response', 'Mass Notification', 'Crisis Management','Information & Communication Technology Management'],
  'Housing': ['Housing', 'Residence Life', 'Dormitory', 'Residential', 'Campus Housing', 'Student Housing', 'Room Assignment', 'Residential Services'],
  'Parking': ['Parking', 'Transportation', 'Vehicle', 'Campus Parking', 'Parking Services', 'Transit', 'Mobility'],
  'Student Conduct': ['Student Conduct', 'Case Management', 'Discipline', 'Judicial Affairs', 'Behavioral', 'Compliance', 'Standards', 'Ethics'],
  'Institutional Infrastructure & Operations': ['Infrastructure', 'Operations', 'IT', 'Technology', 'Systems', 'Integration', 'API', 'Data Exchange', 'Middleware', 'Connectivity', 'Campus Operations', 'Facilities', 'Space Management', 'Human Resources', 'HR', 'Payroll', 'Employee Management', 'Workforce', 'Curriculum Management', 'Course Management', 'Academic Planning', 'Program Management', 'Learning Management', 'LMS', 'Online Learning', 'Distance Learning', 'Course Delivery', 'Teaching & Learning Delivery', 'Student Information', 'SIS', 'Student Records', 'Registration', 'Identity & Access', 'Authentication', 'Single Sign-On', 'SSO', 'Security', 'Analytics & Reporting', 'Business Intelligence', 'Data Analytics', 'Reporting', 'Dashboards', 'Communication', 'Messaging', 'Notifications', 'Email', 'SMS', 'Workforce Relations Management', 'Enterprise Systems', 'Data Management', 'Business Operations']
};

/**
 * Category to logo mapping - Using purple icons
 */
export const categoryLogos = {
  'Academic Support': '/fallback-logos/Purple/Purple/Academic & Student Support Services-Purple.png',
  'Academic Advising': '/fallback-logos/Purple/Purple/Academic Advising & Academic Success-Purple.png',
  'Admissions': '/fallback-logos/Purple/Purple/Admissions_Purple.png',
  'Advancement': '/fallback-logos/Purple/Purple/Advancement and Alumni_Purple.png',
  'Information and Communication Technology Management': '/fallback-logos/Purple/Purple/Emergency Communication-Purple.png',
  'Campus Life & Student Experience': '/fallback-logos/Purple/Purple/Campus Life & Student Experience-Purple.png',
  'Health, Safety & Wellness': '/fallback-logos/Purple/Purple/Health, Safety & Wellness-Purple.png',
  'Medical': '/fallback-logos/Purple/Purple/Medical-Purple.png',
  'Library': '/fallback-logos/Purple/Purple/Library -Purple.png',
  'Bookstore': '/fallback-logos/Purple/Purple/Bookstore-Purple.png',
  'Accessibility': '/fallback-logos/Purple/Purple/Accessibility-Purple.png',
  'Banking & Financial': '/fallback-logos/Purple/Purple/Banking-Purple.png',
  'Career Services': '/fallback-logos/Purple/Purple/Career Services-Purple.png',
  'Emergency Communication': '/fallback-logos/Purple/Purple/Emergency Communication-Purple.png',
  'Housing': '/fallback-logos/Purple/Purple/Housing-Purple.png',
  'Parking': '/fallback-logos/Purple/Purple/Parking-Purple.png',
  'Student Conduct': '/fallback-logos/Purple/Purple/Student Conduct & Case Management-Purple.png',
  'Institutional Infrastructure & Operations': '/fallback-logos/Purple/Purple/Institutional Infrastructure & Operations-Purple.png',
  'Teaching & Learning Delivery': '/fallback-logos/Purple/Purple/Institutional Infrastructure & Operations-Purple.png'
};

/**
 * Automatically categorize a tag based on keyword matching
 * @param tag - The tag to categorize
 * @returns The category group name or null if no match found
 */
export const autoCategorizeTag = (tag: string): string | null => {
  const lowercaseTag = tag.toLowerCase();
  
  for (const [categoryGroup, keywords] of Object.entries(knownCategoryGroups)) {
    for (const keyword of keywords) {
      if (lowercaseTag.includes(keyword.toLowerCase()) || keyword.toLowerCase().includes(lowercaseTag)) {
        return categoryGroup;
      }
    }
  }
  
  return null;
};

/**
 * Generate category groups from a list of tags
 * @param tags - Array of tags to group
 * @returns Object with category groups as keys and arrays of tags as values
 */
export const generateCategoryGroups = (tags: string[]): Record<string, string[]> => {
  const categoryGroups: Record<string, string[]> = {};
  const uncategorized: string[] = [];
  
  for (const tag of tags) {
    const category = autoCategorizeTag(tag);
    if (category) {
      if (!categoryGroups[category]) {
        categoryGroups[category] = [];
      }
      categoryGroups[category].push(tag);
    } else {
      uncategorized.push(tag);
    }
  }
  
  // Add uncategorized tags if any
  if (uncategorized.length > 0) {
    categoryGroups['Other'] = uncategorized;
  }
  
  return categoryGroups;
};

/**
 * Get category name from a tag
 * @param tag - The tag to get category for
 * @returns The category name or 'Other' if no match
 */
export const getCategoryNameFromTag = (tag: string): string => {
  return autoCategorizeTag(tag) || 'Other';
};

/**
 * Get the appropriate logo for a category based on tags
 * @param tags - Array of solution tags
 * @returns Logo path or null if no category-specific logo found
 */
export const getCategoryLogoFromTag = (tags: string[]): string | null => {
  // Try to find the best matching category for the first few tags
  for (const tag of tags.slice(0, 3)) { // Check first 3 tags for performance
    const category = autoCategorizeTag(tag);
    if (category && categoryLogos[category as keyof typeof categoryLogos]) {
      return categoryLogos[category as keyof typeof categoryLogos];
    }
  }
  
  return null;
};